import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { getI18nLanguage } from 'app/lib/language';
import { useAllCategories } from 'app/network/category';
import { createTheme } from 'app/shopify';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet } from 'react-native';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { TagsSectionSkeleton } from './tags-section-skeleton';

const theme = createTheme();

export function PopularCategories({
  onCategoryPress,
  onViewAll,
}: {
  onCategoryPress: (categoryId: string) => void;
  onViewAll: () => void;
}) {
  const { data: categories, isPending } = useAllCategories();
  const { t } = useTranslation('landing-page');
  const language = getI18nLanguage();
  const scrollRef = useRef<ScrollView>(null);
  const offsetRef = useRef(0);
  const [offset, setOffset] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const maxOffset = Math.max(0, contentWidth - viewportWidth);
  const canScrollLeft = offset > 1;
  const canScrollRight = viewportWidth > 0 && offset < maxOffset - 1;

  const scrollTo = (nextOffset: number) => {
    const clamped = Math.min(maxOffset, Math.max(0, nextOffset));
    offsetRef.current = clamped;
    setOffset(clamped);
    scrollRef.current?.scrollTo({ x: clamped, animated: true });
  };
  const resize = (width: number, content: number) => {
    const clamped = Math.min(offsetRef.current, Math.max(0, content - width));
    offsetRef.current = clamped;
    setOffset(clamped);
    scrollRef.current?.scrollTo({ x: clamped, animated: false });
  };

  if (isPending) return <TagsSectionSkeleton chipWidths={[70, 90, 60, 100, 75, 85, 65, 95]} />;
  if (!categories?.length) return null;

  return (
    <Column gap="spacing.general.x3" testID="popular-categories-section">
      <Row style={styles.header}>
        <Text fontSize={20} fontWeight="700" lineHeight={24} variant="bodyBase">
          {t('popularInterests')}
        </Text>
        <Row style={styles.actions}>
          <Pressable accessibilityRole="button" onPress={onViewAll}>
            <Text color="text.interactive" fontSize={14} fontWeight="600">
              {t('viewMore')}
            </Text>
          </Pressable>
          <Row style={styles.arrows}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('carousel.previousSlide', { ns: 'accessibility' })}
              disabled={!canScrollLeft}
              onPress={() => scrollTo(offsetRef.current - viewportWidth * 0.8)}
              style={[styles.arrow, canScrollLeft ? styles.arrowActive : styles.arrowMuted]}
            >
              <IconChevronLeft
                size={20}
                strokeWidth={ICON_STROKE_WIDTH}
                color={theme.colors[canScrollLeft ? 'surface.inverse.tertiary-neutral' : 'text.primary']}
              />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('carousel.nextSlide', { ns: 'accessibility' })}
              disabled={!canScrollRight}
              onPress={() => scrollTo(offsetRef.current + viewportWidth * 0.8)}
              style={[styles.arrow, canScrollRight ? styles.arrowActive : styles.arrowMuted]}
            >
              <IconChevronRight
                size={20}
                strokeWidth={ICON_STROKE_WIDTH}
                color={theme.colors[canScrollRight ? 'surface.inverse.tertiary-neutral' : 'text.primary']}
              />
            </Pressable>
          </Row>
        </Row>
      </Row>
      <ScrollView
        ref={scrollRef}
        testID="popular-categories-scroll"
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.scroll}
        onLayout={({ nativeEvent }) => {
          setViewportWidth(nativeEvent.layout.width);
          resize(nativeEvent.layout.width, contentWidth);
        }}
        onContentSizeChange={(width) => {
          setContentWidth(width);
          resize(viewportWidth, width);
        }}
        onScroll={({ nativeEvent }) => {
          const clamped = Math.min(maxOffset, Math.max(0, nativeEvent.contentOffset.x));
          offsetRef.current = clamped;
          setOffset(clamped);
        }}
        scrollEventThrottle={16}
      >
        <Row style={styles.pills}>
          <CategoryPill label={t('all-listings:filters.all')} onPress={onViewAll} />
          {categories.map((category) => (
            <CategoryPill
              key={category.id}
              label={category.name[language]}
              onPress={() => onCategoryPress(category.id)}
            />
          ))}
        </Row>
      </ScrollView>
    </Column>
  );
}

function CategoryPill({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => [styles.pill, pressed && styles.pressed]}
    >
      <Text color="text.primary" fontSize={14} fontWeight="400" numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing['spacing.general.x2'],
  },
  actions: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing['spacing.general.x4'] },
  arrows: { flexDirection: 'row', gap: theme.spacing['spacing.general.x2'] },
  scroll: { width: '100%' },
  pills: { flexDirection: 'row', gap: theme.spacing['spacing.general.x2'] },
  pill: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: theme.colors['surface.default'],
    borderColor: theme.colors['border.secondary'],
    borderRadius: 24,
    borderWidth: 1,
    height: 48,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
  },
  arrow: {
    width: Number(theme.tokens.sizing['sizing.general.x8']),
    height: Number(theme.tokens.sizing['sizing.general.x8']),
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowActive: { backgroundColor: theme.colors['text.primary'], boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)' },
  arrowMuted: { backgroundColor: theme.colors['surface.tertiary-neutral'] },
  pressed: { opacity: 0.65 },
});
